import { ForwardedRef, useId, useMemo } from "react";
import { useCombobox } from "downshift";
import { useDesignTokens } from "@boi/react-design-tokens";
import { CoreCombobox } from "@boi/components-core";
import { forwardRefGenerics } from "@boi/react-utils";

import * as S from "./Combobox.styled";
import { MAX_LIST_HEIGHT } from "./Combobox.constants";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTop, type InputLabelTopProps } from "../InputLabelTop";
import { ComboboxBase, type ComboboxBaseProps } from "../ComboboxBase";
import { Typography } from "../Typography";
import { buildAlertAssociationProps } from "../Alert/AlertAssociationProps";

export type ComboboxProps<
  T extends CoreCombobox.ComboboxItem = CoreCombobox.ComboboxItem
> = Omit<
  ComboboxBaseProps<T>,
  | "toggleButtonProps"
  | "getToggleButtonProps"
  | "getMenuProps"
  | "isOpen"
  | "renderHeaderContent"
  | "renderListItem"
  | "maxListHeight"
> & {
  labelProps: Omit<
    InputLabelTopProps,
    "id" | "htmlFor" | "onClick" | "labelHintID" | "children"
  >;
  placeholder?: string;
  selectedItem?: T | null | undefined;
  onChange: (selectedItem: T | null | undefined) => void;
  inputValue: string;
  setInputValue: (inputValue: string) => void;
  alertProps?: InputAlertsProps["alerts"][number] | InputAlertsProps["alerts"];
};

function Combobox<
  T extends CoreCombobox.ComboboxItem = CoreCombobox.ComboboxItem
>(props: ComboboxProps<T>, ref: ForwardedRef<HTMLDivElement>) {
  const {
    labelProps,
    placeholder = "Select",
    items,
    selectedItem,
    onChange,
    inputValue,
    setInputValue,
    alertProps,
    testID = "combobox",
    ...comboboxBaseProps
  } = props;

  const { isDisabled } = comboboxBaseProps;

  const filteredItems = useMemo(() => {
    let updatedItems = items;

    if (inputValue.length > 0) {
      updatedItems = updatedItems.filter((item) => {
        const a = item.title.toLowerCase();
        const b = inputValue.trim().toLowerCase();

        return a.includes(b);
      });
    }

    return updatedItems.sort((a, b) => {
      if (a.isPromoted === b.isPromoted) {
        return 0;
      }

      return a.isPromoted ? -1 : 1;
    });
  }, [items, inputValue]);

  const lastItem = filteredItems.at(-1);

  const lastPromotedItem = useMemo(() => {
    return filteredItems.filter((item) => item.isPromoted).at(-1);
  }, [filteredItems]);

  const { tokens } = useDesignTokens();
  const { select } = tokens;

  const {
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    isOpen,
    getItemProps,
    highlightedIndex,
  } = useCombobox({
    onInputValueChange({ inputValue: newInputValue }) {
      setInputValue(newInputValue);
    },
    items: filteredItems,
    selectedItem,
    itemToString: CoreCombobox.itemToString,
    onSelectedItemChange: ({ selectedItem }) => {
      return onChange(selectedItem);
    },
  });

  const labelHintID = useId();
  const { labelHint } = labelProps;
  const safeLabelHintID = labelHint ? labelHintID : undefined;

  const alertId = useId();
  const ariaAlertAssociationProps = buildAlertAssociationProps(
    alertId,
    props,
    safeLabelHintID
  );

  return (
    <div ref={ref}>
      <InputWrapper
        alert={
          alertProps && (
            <div id={alertId} data-testid={`${testID}-alerts`}>
              <InputAlerts
                alerts={Array.isArray(alertProps) ? alertProps : [alertProps]}
              />
            </div>
          )
        }
      >
        <InputLabelTop
          {...labelProps}
          {...getLabelProps()}
          labelHintID={labelHintID}
        >
          <ComboboxBase
            {...comboboxBaseProps}
            items={filteredItems}
            toggleButtonProps={{
              "aria-describedby": safeLabelHintID,
            }}
            getToggleButtonProps={getToggleButtonProps}
            getMenuProps={getMenuProps}
            isOpen={isOpen}
            renderHeaderContent={() => (
              <S.TextInput
                disabled={isDisabled}
                placeholder={placeholder}
                {...getInputProps()}
              />
            )}
            renderListItem={(item, index) => {
              const isSelected = selectedItem === item;
              const isHighlighted = highlightedIndex === index;

              return (
                <S.ListItem
                  key={item.id}
                  $withSeparator={
                    item !== lastItem && item === lastPromotedItem
                  }
                  {...getItemProps({ item, index })}
                  data-hovered={isHighlighted || undefined}
                  // HACK: this tabIndex=-1 makes talkback in chrome work
                  // Chromium issue: https://issues.chromium.org/issues/40260928
                  tabIndex={-1}
                >
                  <S.ListItemContentWrapper
                    $isSelected={isSelected}
                    $isHighlighted={isHighlighted}
                  >
                    <S.ListItemContent $withIcon={item.icon !== undefined}>
                      <S.ListItemTickIconWrapper>
                        {isSelected && (
                          <S.ListItemTickIcon aria-hidden="true" />
                        )}
                      </S.ListItemTickIconWrapper>

                      {item.icon && (
                        <S.ListItemIconWrapper>
                          {item.icon}
                        </S.ListItemIconWrapper>
                      )}

                      <Typography
                        component="div"
                        color="inherit"
                        title={item.title}
                        variant={
                          select.menu.option.inner.text.typography.variant
                        }
                        fontFamily={
                          select.menu.option.inner.text.typography.fontFamily
                        }
                      >
                        {item.title}
                      </Typography>
                    </S.ListItemContent>
                  </S.ListItemContentWrapper>
                </S.ListItem>
              );
            }}
            maxListHeight={MAX_LIST_HEIGHT}
            testID={testID}
            alertAssociationProps={ariaAlertAssociationProps}
          />
        </InputLabelTop>
      </InputWrapper>
    </div>
  );
}

export default forwardRefGenerics(Combobox);
